import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Polyline } from 'react-leaflet';
import L from 'leaflet';
import API_CONFIG from '../config';  // Add this import

// Hide Leaflet attribution watermark
const mapHideAttribution = (
    <style>
        {`
        .leaflet-control-attribution {
            display: none !important;
        }
        
        /* Fix map controls z-index to stay below navbar */
        .leaflet-control-zoom {
            z-index: 100 !important; /* Lower than sidebar (999) and hamburger (1001) */
        }
        
        .leaflet-control {
            z-index: 100 !important; /* All map controls stay below navbar */
        }
        
        /* Ensure map container doesn't interfere */
        .leaflet-container {
            z-index: 1 !important;
        }
        `}
    </style>
);

interface Journey {
    waste_ticket_id: string;
    site_lat: number;
    site_lon: number;
    dropoff_lat: number;
    dropoff_lon: number;
    site_address: string;
    dropoff_address: string;
    description: string;
    site_name?: string;
    dropoff_name?: string;
    vehicle?: string;
    driver_name?: string;
    collection_datetime?: string;
    waste_description: string;
    vehicle_reg: string;
    date?: string;
}

interface WasteTicketMapProps {
    journeys: Journey[];
    onSelectJourney?: (id: string) => void;
}

// Helper to create a larger circle icon with ticket ID
const createTicketIcon = (ticketId: string) =>
    L.divIcon({
        className: '',
        html: `<div style="
            background:#fff;
            border:2px solid #222;
            border-radius:50%;
            width:45px;
            height:45px;
            display:flex;
            align-items:center;
            justify-content:center;
            font-weight:700;
            font-size:1.15em;
            color:#222;
            box-shadow:0 2px 8px rgba(0,0,0,0.12);
        ">${ticketId}</div>`,
        iconSize: [54, 54],
        iconAnchor: [27, 27],
    });

const WasteTicketMap: React.FC<WasteTicketMapProps> = ({ journeys, onSelectJourney }) => {
    const [dialogJourney, setDialogJourney] = useState<Journey | null>(null);
    const [checkingSession, setCheckingSession] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        setCheckingSession(true);

        // Updated to use HTTPS configuration
        axios.get('/api/session', { 
            withCredentials: true,
            baseURL: API_CONFIG.BASE_URL  // https://127.0.0.1:5000
        })
            .then(() => {
                if (cancelled) return;
                setCheckingSession(false);
            })
            .catch(err => {
                if (cancelled) return;
                console.error('Map session check error:', err);
                
                if (err?.response?.status === 401) {
                    try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                    navigate('/login');
                } else if (err.code === 'ECONNREFUSED' || err.message?.includes('ERR_CERT_AUTHORITY_INVALID')) {
                    // HTTPS connection error - still allow map to render if we have journey data
                    console.warn('HTTPS connection failed, but allowing map to render with existing data');
                    setCheckingSession(false);
                } else {
                    // Other non-auth error: stop checking so map can render if journeys provided
                    setCheckingSession(false);
                }
            });

        return () => { cancelled = true; };
    }, [navigate]);

    if (checkingSession) {
        return (
            <div style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f5f5f5',
                borderRadius: '8px',
                fontSize: '1.1em',
                color: '#666'
            }}>
                🔒 Establishing secure connection...
            </div>
        );
    }

    if (!journeys || journeys.length === 0) {
        return (
            <div style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f5f5f5',
                borderRadius: '8px',
                fontSize: '1.1em',
                color: '#666'
            }}>
                📍 No waste ticket locations to display
            </div>
        );
    }

    // Calculate map center as average of all points
    const allPoints = journeys.flatMap(j => [
        [j.site_lat, j.site_lon],
        [j.dropoff_lat, j.dropoff_lon]
    ]);
    const center = [
        allPoints.reduce((sum, p) => sum + p[0], 0) / allPoints.length,
        allPoints.reduce((sum, p) => sum + p[1], 0) / allPoints.length,
    ];

    // Dialog for journey details
    const renderDialog = () => {
        if (!dialogJourney) return null;
        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    background: 'rgba(0,0,0,0.18)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000
                }}
                onClick={e => {
                    if (e.target === e.currentTarget) setDialogJourney(null);
                }}
            >
                <div
                    style={{
                        background: '#fff',
                        padding: '32px 36px',
                        borderRadius: '18px',
                        boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
                        minWidth: '370px',
                        maxWidth: '95vw',
                        textAlign: 'left',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '14px',
                        position: 'relative'
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontWeight: 600, fontSize: '1.25em', color: '#115740', marginRight: 8 }}>
                            Waste Ticket: {dialogJourney.waste_ticket_id}
                        </span>
                        <button
                            type="button"
                            onClick={() => setDialogJourney(null)}
                            style={{
                                marginLeft: 'auto',
                                background: 'none',
                                border: 'none',
                                fontSize: '1.5em',
                                color: '#888',
                                cursor: 'pointer',
                                padding: 0
                            }}
                            aria-label="Close"
                        >
                            ×
                        </button>
                    </div>
                    <div><strong>Description:</strong> {dialogJourney.waste_description}</div>
                    <div><strong>Site:</strong> {dialogJourney.site_name} ({dialogJourney.site_address})</div>
                    <div><strong>Dropoff:</strong> {dialogJourney.dropoff_name} ({dialogJourney.dropoff_address})</div>
                    <div><strong>Vehicle:</strong> {dialogJourney.vehicle_reg}</div>
                    <div><strong>Driver:</strong> {dialogJourney.driver_name}</div>
                    <div><strong>Date:</strong> {dialogJourney.date}</div>
                    
                </div>
            </div>
        );
    };

    return (
        <>
            {mapHideAttribution}
            <MapContainer center={center as [number, number]} zoom={10} style={{ height: '100%', width: '100%' }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {journeys.map(journey => (
                    <React.Fragment key={journey.waste_ticket_id}>
                        <Marker
                            position={[journey.site_lat, journey.site_lon]}
                            icon={createTicketIcon(journey.waste_ticket_id)}
                            eventHandlers={{
                                click: () => {
                                    setDialogJourney(journey);
                                    if (onSelectJourney) onSelectJourney(journey.waste_ticket_id);
                                }
                            }}
                        />
                        <Marker
                            position={[journey.dropoff_lat, journey.dropoff_lon]}
                            icon={createTicketIcon(journey.waste_ticket_id)}
                            eventHandlers={{
                                click: () => {
                                    setDialogJourney(journey);
                                    if (onSelectJourney) onSelectJourney(journey.waste_ticket_id);
                                }
                            }}
                        />
                        <Polyline
                            positions={[[journey.site_lat, journey.site_lon], [journey.dropoff_lat, journey.dropoff_lon]]}
                            color="blue"
                            eventHandlers={{
                                click: () => {
                                    setDialogJourney(journey);
                                    if (onSelectJourney) onSelectJourney(journey.waste_ticket_id);
                                }
                            }}
                        />
                    </React.Fragment>
                ))}
            </MapContainer>
            {renderDialog()}
        </>
    );
};

export default WasteTicketMap;